<script lang="ts" setup>
import { ref, onMounted, watch } from 'vue';
import PageSection from '@/components/layouts/PageSection.vue';
import ContentWithLoadingSkeleton from '@/components/ContentLoadingSkeleton/ContentWithLoadingSkeleton.vue';
import { fetchFolders, fetchFolderArticles, searchArticles } from '@/helpers/api/knowledgeBase/freshdeskKnowledgeBase';
import type { Folder, Article } from '@/helpers/api/knowledgeBase/types';

const folders = ref<Folder[]>([]);
const folderArticles = ref<Record<number, Article[]>>({});
const isLoadingArticles = ref<Record<number, boolean>>({});
const isLoading = ref(false);
const searchTerm = ref('');
const searchResults = ref<Article[]>([]);
const hasSearched = ref(false);

async function loadFolders() {
  isLoading.value = true;
  try {
    folders.value = await fetchFolders();
    console.log('Folders loaded:', folders.value);
    console.log('Number of folders:', folders.value.length);

    // Load articles for each folder
    folders.value.forEach(folder => {
      console.log(`Folder ${folder.id}: ${folder.name} has ${folder.articles_count} articles`);
      if (folder.articles_count > 0) {
        loadFolderArticles(folder.id);
      }
    });

  } catch (error) {
    console.error('Failed to load folders:', error);
  } finally {
    isLoading.value = false;
    console.log('isLoading set to false');
  }

}

async function loadFolderArticles(folderId: number) {
  isLoadingArticles.value[folderId] = true;
  console.log(`Loading articles for folder ${folderId}...`);
  try {
    const articles = await fetchFolderArticles(folderId);
    console.log(`Folder ${folderId} loaded ${articles.length} articles:`, articles);
    folderArticles.value[folderId] = articles;
    console.log('folderArticles state:', folderArticles.value);
  } catch (error) {
    console.error(`Failed to load articles for folder ${folderId}:`, error);
  } finally {
    isLoadingArticles.value[folderId] = false;
    console.log(`Finished loading articles for folder ${folderId}`);
  }
}

async function handleSearch() {
  if (!searchTerm.value.trim()) {
    searchResults.value = [];
    hasSearched.value = false;
    return;
  }

  isLoading.value = true;
  hasSearched.value = true;
  try {
    searchResults.value = await searchArticles(searchTerm.value);
  } catch (error) {
    console.error('Failed to search articles:', error);
    searchResults.value = [];
  } finally {
    isLoading.value = false;
  }
}

watch(searchTerm, (newValue) => {
  if (!newValue.trim()) {
    searchResults.value = [];
    hasSearched.value = false;
  }
});

onMounted(() => {
  loadFolders();
});
</script>
<template>
  <div class="knowledge-base-view">
    <PageSection separator="none" spacing="bottom">
      <div class="top-bar">
        <h1 class="title">Knowledge base</h1>
      </div>
    </PageSection>

    <PageSection separator="top" spacing="both">
      <div class="search-section">
        <div class="search-container">
          <input v-model="searchTerm" type="text" placeholder="Enter your search term here..." class="search-input"
            @keyup.enter="handleSearch" />
          <button class="search-button" @click="handleSearch">
            SEARCH
          </button>
        </div>
      </div>
    </PageSection>
    <PageSection separator="top" spacing="both">

      <ContentWithLoadingSkeleton :isLoading="isLoading">
        <div v-if="searchResults.length > 0" class="search-results">

          <h2 class="section-title">Search Results</h2>
          <div class="articles-list">
            <div v-for="article in searchResults" :key="article.id" class="article-item">
              <RouterLink :to="`/knowledge-base/article/${article.id}`" class="article-link">
                <span class="article-icon">📄</span>
                {{ article.title }}
              </RouterLink>
              <div v-if="article.folder_name" class="article-folder">
                {{ article.folder_name }}
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="hasSearched && searchResults.length === 0" class="no-results">
          <h2 class="section-title">No Results Found</h2>
          <p class="no-results-message">No articles found matching "{{ searchTerm }}". Try a different search term.</p>
        </div>

        <div v-else class="folders-container">

          <div v-for="folder in folders" :key="folder.id" class="folder-section">
            <h2 class="folder-title">
              {{ folder.name }}
              <span class="article-count">({{ folder.articles_count }})</span>
            </h2>
            <div v-if="folderArticles[folder.id]" class="articles-list">
              <div v-for="article in folderArticles[folder.id]" :key="article.id" class="article-item">
                <RouterLink :to="`/knowledge-base/article/${article.id}`" class="article-link">
                  <span class="article-icon">📄</span>
                  {{ article.title }}
                </RouterLink>
              </div>
            </div>
            <div v-else-if="isLoadingArticles[folder.id]" class="loading-articles">
              Loading articles...
            </div>
          </div>
        </div>

      </ContentWithLoadingSkeleton>
    </PageSection>
  </div>
</template>

<style lang="scss" scoped>
.knowledge-base-view {
  .top-bar {
    display: flex;
    align-items: center;
    gap: 1em;
  }

  h1.title {
    font-size: 3em;
    font-weight: bold;
    text-transform: none;
    color: #252949;
    letter-spacing: 2px;
    border-bottom: 2px solid #1b4187;
    padding-bottom: 0.2em;
    display: inline-block;
  }

  .search-section {
    margin-bottom: 2rem;
  }

  .search-container {
    display: flex;
    gap: 1rem;
    align-items: center;
    max-width: 800px;
  }

  .search-input {
    flex: 1;
    padding: 0.75rem 1rem;
    border: 1px solid #e6e6e6;
    border-radius: 4px;
    font-size: 1rem;
    min-width: 300px;

    &:focus {
      outline: none;
      border-color: #1a4285;
    }
  }

  .search-button {
    padding: 0.75rem 2rem;
    background-color: #25294A;
    color: #ffffff;
    border: none;
    border-radius: 4px;
    font-weight: bold;
    text-transform: uppercase;
    cursor: pointer;
    box-shadow: 0px 3px 9px rgb(0 0 0 / 16%);
    transition: background-color 0.2s ease-in-out;

    &:hover {
      background-color: #B1C4CF;
    }
  }

  .section-title {
    font-size: 2em;
    font-weight: bold;
    color: #252949;
    margin-bottom: 1.5rem;
    text-transform: none;
  }

  .folders-container {
    display: flex;
    flex-direction: column;
    gap: 2.5rem;
  }

  .folder-section {
    .folder-title {
      font-size: 1.5em;
      font-weight: bold;
      color: #252949;
      margin-bottom: 1rem;
      text-transform: none;
      letter-spacing: 1px;

      .article-count {
        font-weight: normal;
        color: #666;
      }
    }
  }

  .articles-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1rem;
    margin-top: 1rem;

    @include medium-devices-down {
      grid-template-columns: 1fr;
    }
  }

  .article-item {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .article-link {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #1c69d4;
    text-decoration: none;
    font-size: 1rem;
    transition: color 0.2s ease-in-out;

    &:hover {
      color: #114f75;
      text-decoration: underline;
    }

    .article-icon {
      font-size: 0.9em;
      opacity: 0.7;
    }
  }

  .article-folder {
    font-size: 0.85em;
    color: #666;
    margin-left: 1.5rem;
  }

  .loading-articles {
    color: #666;
    font-style: italic;
    margin-top: 0.5rem;
  }

  .no-results {
    text-align: center;
    padding: 3rem 1rem;

    .section-title {
      color: #666;
      margin-bottom: 1rem;
    }

    .no-results-message {
      color: #999;
      font-size: 1.1rem;
      line-height: 1.6;
    }
  }

  .search-results {
    .articles-list {
      grid-template-columns: 1fr;
    }
  }
}
</style>
